import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/ui/Button';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './HowWeWork.scss';

const steps = [
  {
    number: '01',
    title: 'Discovery Call',
    description: 'I start with a conversation to understand your goals, constraints, and timeline. No commitment required.',
  },
  {
    number: '02',
    title: 'Scope & Estimate',
    description: 'I define the project scope and provide a fixed-bid or time-and-materials estimate based on your preference.',
  },
  {
    number: '03',
    title: 'Build in Milestones',
    description: 'Development happens in clear milestones with regular check-ins so you always know where things stand.',
  },
  {
    number: '04',
    title: 'Review & Demo',
    description: 'Each milestone ends with a demo. You review, give feedback, and I iterate until it meets your standards.',
  },
  {
    number: '05',
    title: 'Handoff & Support',
    description: 'I deliver clean, documented code and offer ongoing support options if you need them.',
  },
];

function HowWeWork() {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [stepsRef, stepsVisible] = useScrollAnimation<HTMLDivElement>();
  const [ctaRef, ctaVisible] = useScrollAnimation<HTMLDivElement>();

  useEffect(() => {
    const timer = setTimeout(() => setHeaderVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>How I Work | Valendris</title>
        <meta name="description" content="A straightforward process designed for clarity and accountability. Milestone-based development with regular demos and clear communication." />
      </Helmet>
      <section className="how-we-work">
        <div className="how-we-work-container container">
          <header className={`how-we-work-header ${headerVisible ? 'visible' : ''}`}>
            <h1 className="how-we-work-title">How I Work</h1>
            <p className="how-we-work-subtitle">
              A straightforward process designed for clarity and accountability.
            </p>
          </header>

          <div
            ref={stepsRef}
            className={`how-we-work-steps ${stepsVisible ? 'visible' : ''}`}
          >
            <div className="how-we-work-timeline" />
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="how-we-work-step"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="how-we-work-step-marker">
                  <span className="how-we-work-step-number">{step.number}</span>
                </div>
                <div className="how-we-work-step-content">
                  <h2 className="how-we-work-step-title">{step.title}</h2>
                  <p className="how-we-work-step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={ctaRef}
            className={`how-we-work-cta ${ctaVisible ? 'visible' : ''}`}
          >
            <p className="how-we-work-cta-text">Ready to discuss your project?</p>
            <Button to="/contact">Get in Touch</Button>
          </div>
        </div>
      </section>
    </>
  );
}

export default HowWeWork;
